import { AlertTriangle, FlaskConical, Pill, Tag } from 'lucide-react';

import type { RxMedication } from '@/entities/rx';
import { cn } from '@/lib/utils';

import { isMultiIngredient } from '../model/formularyDisplayPolicy';

export interface MedicationMetaBadgesProps {
  medication: RxMedication | null | undefined;
}

/**
 * Compact badge row summarising the cert-mandated drug-level meta:
 *
 * - **Brand vs Generic** (cert §1.1)
 * - **Rx vs OTC** (cert §1.2)
 * - **Schedule** (II/III/IV/V) when applicable (cert §1.3)
 * - **Obsolete** marker (cert §1.4) — also blocks alternatives display
 * - **Multi-ingredient** marker (cert §1.5) — drives alt-filter rule
 *
 * Rendered just under the formulary status badge on the Details step.
 * Each badge is plain text; we don't try to over-design — the cert
 * cares about correct identification, not aesthetics.
 */
export function MedicationMetaBadges({ medication }: MedicationMetaBadgesProps) {
  if (!medication) return null;

  const items = collectBadges(medication);
  if (items.length === 0) return null;

  return (
    <div className="flex flex-wrap items-center gap-1">
      {items.map((item) => (
        <span
          key={item.key}
          className={cn(
            'inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] font-medium',
            item.tone
          )}
        >
          {item.icon}
          {item.label}
        </span>
      ))}
    </div>
  );
}

interface BadgeItem {
  key: string;
  label: string;
  tone: string;
  icon: React.ReactNode;
}

const TONE_NEUTRAL =
  'border-border/60 bg-muted/40 text-foreground/80 dark:border-border/40 dark:bg-muted/20';
const TONE_INFO =
  'border-sky-400/40 bg-sky-50 text-sky-900 dark:border-sky-500/30 dark:bg-sky-950/40 dark:text-sky-100';
const TONE_WARN =
  'border-amber-400/50 bg-amber-50 text-amber-900 dark:border-amber-500/30 dark:bg-amber-950/40 dark:text-amber-100';
const TONE_DANGER =
  'border-red-400/50 bg-red-50 text-red-900 dark:border-red-500/30 dark:bg-red-950/40 dark:text-red-100';

function collectBadges(medication: RxMedication): BadgeItem[] {
  const badges: BadgeItem[] = [];

  if (medication.Brand === true) {
    badges.push({
      key: 'brand',
      label: 'Brand',
      tone: TONE_INFO,
      icon: <Pill className="size-3" aria-hidden />,
    });
  } else if (medication.Brand === false) {
    badges.push({
      key: 'generic',
      label: 'Generic',
      tone: TONE_NEUTRAL,
      icon: <Pill className="size-3" aria-hidden />,
    });
  }

  if (medication.OTC === true) {
    badges.push({
      key: 'otc',
      label: 'OTC',
      tone: TONE_NEUTRAL,
      icon: <Tag className="size-3" aria-hidden />,
    });
  } else if (medication.OTC === false) {
    badges.push({
      key: 'rx',
      label: 'Rx-Only',
      tone: TONE_NEUTRAL,
      icon: <Tag className="size-3" aria-hidden />,
    });
  }

  const schedule = scheduleLabel(medication.Schedule);
  if (schedule != null) {
    badges.push({
      key: 'schedule',
      label: schedule,
      tone: TONE_WARN,
      icon: <FlaskConical className="size-3" aria-hidden />,
    });
  }

  if (medication.IsObsolete === true) {
    badges.push({
      key: 'obsolete',
      label: 'Obsolete',
      tone: TONE_DANGER,
      icon: <AlertTriangle className="size-3" aria-hidden />,
    });
  }

  if (isMultiIngredient(medication)) {
    badges.push({
      key: 'multi-ingredient',
      label: 'Multi-ingredient',
      tone: TONE_NEUTRAL,
      icon: <FlaskConical className="size-3" aria-hidden />,
    });
  }

  return badges;
}

/**
 * Map DoseSpot's integer ``Schedule`` (1-5 for federal schedule) to a
 * Roman-numeral label. Schedule 0 / null = "not scheduled" → no badge.
 */
function scheduleLabel(schedule: number | null | undefined): string | null {
  if (schedule == null || schedule <= 0 || schedule > 5) return null;
  const roman = ['I', 'II', 'III', 'IV', 'V'][schedule - 1];
  return `Schedule ${roman}`;
}
